import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Shield, FileText, Download, RefreshCw, Clock, ChevronDown, ChevronRight, AlertTriangle, CheckCircle, Activity } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ComplianceAdmin() {
  const [documents, setDocuments] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [securityReports, setSecurityReports] = useState([]);
  const [expandedDoc, setExpandedDoc] = useState(null);
  const [expandedReport, setExpandedReport] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [runningDaily, setRunningDaily] = useState(false);
  const [runningWeekly, setRunningWeekly] = useState(false);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [docsRes, logsRes, reportsRes] = await Promise.all([
        axios.get(`${API}/api/admin/compliance/documents`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/api/admin/audit-log?limit=20`, { headers }).catch(() => ({ data: { total: 0, logs: [] } })),
        axios.get(`${API}/api/admin/security/reports?limit=5`, { headers }).catch(() => ({ data: [] })),
      ]);
      setDocuments(docsRes.data || []);
      setAuditLogs(logsRes.data?.logs || []);
      setAuditTotal(logsRes.data?.total || 0);
      setSecurityReports(reportsRes.data || []);
    } catch (err) {
      console.error('Failed to load compliance data:', err);
    } finally {
      setLoading(false);
    }
  };

  const generateDocs = async () => {
    setGenerating(true);
    try {
      await axios.post(`${API}/api/admin/compliance/generate`, {}, { headers });
      toast.success('Compliance documents generated');
      loadAll();
    } catch (err) {
      toast.error('Failed to generate documents');
    } finally {
      setGenerating(false);
    }
  };

  const runDailyCheck = async () => {
    setRunningDaily(true);
    try {
      await axios.post(`${API}/api/admin/security/run-daily`, {}, { headers });
      toast.success('Daily security check started — results in ~10 seconds');
      setTimeout(loadAll, 12000);
    } catch (err) {
      toast.error('Failed to start daily check');
    } finally {
      setRunningDaily(false);
    }
  };

  const runWeeklyCheck = async () => {
    setRunningWeekly(true);
    try {
      await axios.post(`${API}/api/admin/security/run-weekly`, {}, { headers });
      toast.success('Weekly security scan started — results in ~15 seconds');
      setTimeout(loadAll, 18000);
    } catch (err) {
      toast.error('Failed to start weekly scan');
    } finally {
      setRunningWeekly(false);
    }
  };

  const downloadDoc = (doc) => {
    const blob = new Blob([doc.content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${doc.doc_id}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="w-6 h-6 border-2 border-[#00F0FF] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
        <p className="text-neutral-500 text-sm">Loading compliance data...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="compliance-admin">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield className="w-5 h-5 text-[#00F0FF]" />
          <h2 className="text-lg font-bold text-white">Compliance & Security</h2>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={runDailyCheck}
            disabled={runningDaily}
            size="sm"
            variant="outline"
            className="border-white/10 text-xs h-8"
            data-testid="run-daily-check-btn"
          >
            {runningDaily ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <Activity className="w-3 h-3 mr-1" />}
            Run Daily Check
          </Button>
          <Button
            onClick={runWeeklyCheck}
            disabled={runningWeekly}
            size="sm"
            variant="outline"
            className="border-white/10 text-xs h-8"
            data-testid="run-weekly-check-btn"
          >
            {runningWeekly ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <Shield className="w-3 h-3 mr-1" />}
            Run Full Scan
          </Button>
        </div>
      </div>

      {/* Security Reports */}
      <div className="rounded-xl bg-neutral-900/50 border border-white/10 p-4">
        <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
          <Activity className="w-4 h-4 text-[#00F0FF]" />
          Security Scan Reports
        </h3>
        {securityReports.length === 0 ? (
          <p className="text-xs text-neutral-500">No scans run yet. Click "Run Daily Check" to start.</p>
        ) : (
          <div className="space-y-2">
            {securityReports.map((report) => (
              <div key={report.report_id} className="border border-white/5 rounded-lg overflow-hidden">
                <div
                  className="flex items-center justify-between p-3 cursor-pointer hover:bg-white/5"
                  onClick={() => setExpandedReport(expandedReport === report.report_id ? null : report.report_id)}
                >
                  <div className="flex items-center gap-3">
                    {report.status === 'PASS' ? (
                      <CheckCircle className="w-4 h-4 text-green-400" />
                    ) : (
                      <AlertTriangle className="w-4 h-4 text-red-400" />
                    )}
                    <div>
                      <span className="text-sm font-medium text-white">{report.scan_type}</span>
                      <span className="text-xs text-neutral-500 ml-2">
                        {new Date(report.created_at).toLocaleString()}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${report.status === 'PASS' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                      {report.passed}/{report.total_checks} Passed
                    </span>
                    {expandedReport === report.report_id ? <ChevronDown className="w-4 h-4 text-neutral-500" /> : <ChevronRight className="w-4 h-4 text-neutral-500" />}
                  </div>
                </div>
                {expandedReport === report.report_id && (
                  <div className="border-t border-white/5 p-3 space-y-1">
                    {report.results?.map((r, idx) => (
                      <div key={idx} className="flex items-start gap-2 text-xs">
                        {r.passed ? (
                          <CheckCircle className="w-3.5 h-3.5 text-green-400 mt-0.5 flex-shrink-0" />
                        ) : (
                          <AlertTriangle className="w-3.5 h-3.5 text-red-400 mt-0.5 flex-shrink-0" />
                        )}
                        <div>
                          <span className="font-medium text-neutral-300">{r.name}</span>
                          <span className="text-neutral-500 ml-1">— {r.detail}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Compliance Documents */}
      <div className="rounded-xl bg-neutral-900/50 border border-white/10 p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-white flex items-center gap-2">
            <FileText className="w-4 h-4 text-white" />
            Compliance Documents ({documents.length})
          </h3>
          <Button
            onClick={generateDocs}
            disabled={generating}
            size="sm"
            className="bg-white hover:bg-neutral-200 text-black text-xs h-8"
            data-testid="generate-docs-btn"
          >
            {generating ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <FileText className="w-3 h-3 mr-1" />}
            {documents.length > 0 ? 'Regenerate' : 'Generate'} Documents
          </Button>
        </div>
        {documents.length === 0 ? (
          <p className="text-xs text-neutral-500">No documents yet. Click "Generate Documents" to create SOC 2 compliance docs.</p>
        ) : (
          <div className="space-y-2">
            {documents.map((doc) => (
              <div key={doc.doc_id} className="border border-white/5 rounded-lg overflow-hidden">
                <div
                  className="flex items-center justify-between p-3 cursor-pointer hover:bg-white/5"
                  onClick={() => setExpandedDoc(expandedDoc === doc.doc_id ? null : doc.doc_id)}
                >
                  <div className="flex items-center gap-3">
                    <FileText className="w-4 h-4 text-neutral-400" />
                    <div>
                      <span className="text-sm font-medium text-white">{doc.title}</span>
                      <span className="text-xs text-neutral-500 ml-2">v{doc.version}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => { e.stopPropagation(); downloadDoc(doc); }}
                      className="h-7 w-7 p-0 text-neutral-400 hover:text-white"
                      data-testid={`download-doc-${doc.doc_id}`}
                    >
                      <Download className="w-3.5 h-3.5" />
                    </Button>
                    {expandedDoc === doc.doc_id ? <ChevronDown className="w-4 h-4 text-neutral-500" /> : <ChevronRight className="w-4 h-4 text-neutral-500" />}
                  </div>
                </div>
                {expandedDoc === doc.doc_id && (
                  <div className="border-t border-white/5 p-4 max-h-96 overflow-y-auto">
                    <pre className="text-xs text-neutral-300 whitespace-pre-wrap font-sans leading-relaxed">{doc.content}</pre>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Audit Log */}
      <div className="rounded-xl bg-neutral-900/50 border border-white/10 p-4">
        <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
          <Clock className="w-4 h-4 text-yellow-400" />
          Audit Trail ({auditTotal} total events)
        </h3>
        {auditLogs.length === 0 ? (
          <p className="text-xs text-neutral-500">No audit events recorded yet.</p>
        ) : (
          <div className="space-y-1 max-h-72 overflow-y-auto">
            {auditLogs.map((log, idx) => (
              <div key={`${log.timestamp}-${idx}`} className="flex items-start gap-3 text-xs p-2 rounded-lg hover:bg-white/5">
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase flex-shrink-0 mt-0.5 ${
                  log.severity === 'critical' ? 'bg-red-500/20 text-red-400' :
                  log.severity === 'warning' ? 'bg-yellow-500/20 text-yellow-400' :
                  'bg-neutral-700 text-neutral-400'
                }`}>
                  {log.severity}
                </span>
                <div className="min-w-0 flex-1">
                  <span className="text-neutral-300 font-medium">{log.action}</span>
                  <span className="text-neutral-500 ml-2">by {log.actor_email}</span>
                  {log.ip_address && <span className="text-neutral-600 ml-2">({log.ip_address})</span>}
                </div>
                <span className="text-neutral-600 flex-shrink-0">{new Date(log.timestamp).toLocaleString()}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
